@import '../../styles/colors.css';
@import '../../styles/layout.css';
@import '../../styles/radius.css';
@import '../../styles/typography.css';
@import '../../styles/shadow.css';
@import '../../styles/border.css';

:host {
  display: block;
  pointer-events: none;

  /* Background colors */
  --totem-background-color-button-primary-default: var(--totem-color-primary-500);
  --totem-background-color-button-primary-hover: var(--totem-color-primary-600);
  --totem-background-color-button-primary-pressing: var(--totem-color-primary-700);
  --totem-background-color-button-primary-focus: var(--totem-color-primary-500);
  --totem-background-color-button-secondary-default: var(--totem-color-secondary-500);
  --totem-background-color-button-secondary-hover: var(--totem-color-secondary-600);
  --totem-background-color-button-secondary-pressing: var(--totem-color-secondary-700);
  --totem-background-color-button-secondary-focus: var(--totem-color-secondary-500);
  --totem-background-color-button-negative-default: var(--totem-color-status-error-500);
  --totem-background-color-button-negative-hover: var(--totem-color-status-error-600);
  --totem-background-color-button-negative-pressing: var(--totem-color-status-error-700);
  --totem-background-color-button-negative-focus: var(--totem-color-status-error-500);
  --totem-background-color-button-positive-default: var(--totem-color-status-success-500);
  --totem-background-color-button-positive-hover: var(--totem-color-status-success-600);
  --totem-background-color-button-positive-pressing: var(--totem-color-status-success-700);
  --totem-background-color-button-positive-focus: var(--totem-color-status-success-500);
  --totem-background-color-button-disabled: var(--totem-color-tertiary-200);

  /* Text colors */
  --totem-text-color-button-light: var(--totem-color-white);
  --totem-text-color-button-contrast: var(--totem-color-secondary-500);
  --totem-text-color-button-disabled: var(--totem-color-tertiary-400);
  --totem-text-color-button-link-hover: var(--totem-color-secondary-600);
  --totem-text-color-button-link-pressing: var(--totem-color-secondary-700);
  --totem-text-color-button-link-focus: var(--totem-color-secondary-500);
  --totem-shadow-button: var(--totem-x-position-1) var(--totem-y-position-4) var(--totem-blur-7) var(--totem-spread-1) var(--totem-shadow-color-400);

  /* Border colors */
  --totem-border-color-button-focus-light: var(--totem-color-white);
  --totem-border-color-button-focus-contrast: var(--totem-color-black);
  --totem-border-color-button-tertiary-default: var(--totem-color-secondary-500);
  --totem-border-color-button-tertiary-hover: var(--totem-color-secondary-600);
  --totem-border-color-button-tertiary-pressing: var(--totem-color-secondary-700);
  --totem-border-color-button-tertiary-focus: var(--totem-color-secondary-500);
}

:host([full-width='']),
:host([full-width='true']),
:host([full-width='']) .button,
:host([full-width='true']) .button {
  width: 100%;
  max-width: none;
}

:host([size='medium']) ::slotted([slot='start-icon']),
:host([size='medium']) ::slotted([slot='end-icon']),
:host([size='large']) ::slotted([slot='start-icon']),
:host([size='large']) ::slotted([slot='end-icon']) {
  font-size: 24px;
  width: 24px;
  height: 24px;
}

::slotted([slot='start-icon']),
::slotted([slot='end-icon']) {
  font-size: 18px;
  width: 18px;
  height: 18px;
}

::slotted([slot='start-icon'])::before,
::slotted([slot='end-icon'])::before {
  display: inline-block;
}

a.button {
  height: inherit;
  text-decoration: none;
  &_quaternary {
    text-decoration: underline;
  }
}

.button {
  font-family: var(--totem-font-family-web);
  color: var(--totem-text-color-button-light);
  border: none;
  border-radius: var(--totem-radius-3);
  cursor: pointer;
  user-select: none;
  display: inline-flex;
  max-width: 360px;
  justify-content: center;
  align-items: center;
  gap: var(--totem-space-5);
  background-color: unset;
  pointer-events: all;
  transition: 0.2s ease-in-out;
  transition-property: background, box-shadow, color;

  &:focus {
    outline-offset: -4px;
    outline: var(--totem-border-500) solid var(--totem-border-color-button-focus-light);
  }
  &:focus:not(:focus-visible) {
    outline: none;
  }
  &:hover {
    box-shadow: var(--totem-shadow-button);
  }

  &_primary {
    background-color: var(--totem-background-color-button-primary-default);

    &:hover {
      background-color: var(--totem-background-color-button-primary-hover);
    }
    &:active {
      background-color: var(--totem-background-color-button-primary-pressing);
    }
    &:focus {
      background-color: var(--totem-background-color-button-primary-focus);
    }
  }
  &_secondary {
    background-color: var(--totem-background-color-button-secondary-default);

    &:hover {
      background-color: var(--totem-background-color-button-secondary-hover);
    }
    &:active {
      background-color: var(--totem-background-color-button-secondary-pressing);
    }
    &:focus {
      background-color: var(--totem-background-color-button-secondary-focus);
    }
  }
  &_tertiary {
    color: var(--totem-text-color-button-contrast);
    box-sizing: border-box;
    border: var(--totem-border-300) solid var(--totem-border-color-button-tertiary-default);

    &:hover {
      border-color: var(--totem-border-color-button-tertiary-hover);
      box-shadow: none;
    }
    &:active {
      border-color: var(--totem-border-color-button-tertiary-pressing);
    }
    &:focus {
      border-color: var(--totem-border-color-button-tertiary-focus);
      outline-color: var(--totem-border-color-button-focus-contrast);
    }
  }
  &_quaternary {
    color: var(--totem-text-color-button-contrast);
    text-decoration: underline;
    &:hover {
      color: var(--totem-text-color-button-link-hover);
      box-shadow: none;
    }
    &:active {
      color: var(--totem-text-color-button-link-pressing);
    }
    &:focus {
      color: var(--totem-text-color-button-link-focus);
      outline-color: var(--totem-border-color-button-focus-contrast);
    }
  }
  &_negative {
    background-color: var(--totem-background-color-button-negative-default);

    &:hover {
      background-color: var(--totem-background-color-button-negative-hover);
    }
    &:active {
      background-color: var(--totem-background-color-button-negative-pressing);
    }
    &:focus {
      background-color: var(--totem-background-color-button-negative-focus);
    }
  }
  &_positive {
    background-color: var(--totem-background-color-button-positive-default);

    &:hover {
      background-color: var(--totem-background-color-button-positive-hover);
    }
    &:active {
      background-color: var(--totem-background-color-button-positive-pressing);
    }
    &:focus {
      background-color: var(--totem-background-color-button-positive-focus);
    }
  }

  &_small {
    padding: var(--totem-space-5) var(--totem-space-7);
    font-size: var(--totem-font-size-100);
    font-weight: var(--totem-font-weight-600);
    line-height: var(--totem-font-lh-100);
    height: 36px;
  }
  &_medium {
    padding: var(--totem-space-6) var(--totem-space-11);
    font-size: var(--totem-font-size-300);
    font-weight: var(--totem-font-weight-600);
    line-height: var(--totem-font-lh-200);
    height: 44px;
    max-width: 550px;
  }
  &_large {
    padding: var(--totem-space-9) var(--totem-space-11);
    font-size: var(--totem-font-size-400);
    font-weight: var(--totem-font-weight-600);
    line-height: var(--totem-font-lh-200);
    height: 56px;
    max-width: 550px;
  }

  &_disabled {
    pointer-events: none;
    color: var(--totem-text-color-button-disabled);
    background-color: var(--totem-background-color-button-disabled);
  }
}
